import * as React from 'react';

// Field checks shown when the person leaves a field (on blur), not after they press Submit. A rule returns the message
// to show, or null when the value is fine. Once a field has been left, its message updates as the person types, so it
// disappears the moment the value is fixed. Submit calls `touchAll()` as a safety net: anything never visited is
// checked then. The server validates everything again.

export type Rules<K extends string> = Record<K, () => string | null>;

export function useBlurValidation<K extends string>(rules: Rules<K>) {
  const [touched, setTouched] = React.useState<ReadonlySet<K>>(new Set());
  const keys = Object.keys(rules) as K[];

  const errorOf = (k: K): string | null => (touched.has(k) ? rules[k]() : null);
  const touch = (k: K) => setTouched(t => (t.has(k) ? t : new Set(t).add(k)));

  /** Spread onto an input: checks it when the person leaves it, marks it invalid for screen readers and styling. */
  const field = (k: K, id: string) => {
    const error = errorOf(k);
    return {
      onBlur: () => touch(k),
      'aria-invalid': error ? true : undefined,
      'aria-describedby': error ? `${id}-error` : undefined,
    } as const;
  };

  /** On submit: show every message; true when the form is clean. */
  const touchAll = (): boolean => {
    setTouched(new Set(keys));
    return keys.every(k => !rules[k]());
  };

  const reset = () => setTouched(new Set());
  return { errorOf, field, touch, touchAll, reset, isValid: keys.every(k => !rules[k]()) };
}
